import React, { useState, useRef } from "react";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faCopy } from "@fortawesome/free-solid-svg-icons";

import "../css/DataTable.css";

const DataTable = ({
    columns,
    data,
    pagination = false,
    highlightOnHover = false,
    striped = false,
    responsive = false,
    noDataComponent = "No data",
    defaultSort = { key: null, direction: null },
    paginationComponentOptions = {
        rowsPerPageText: "Rows per page",
        rangeSeparatorText: "of",
    },
    paginationMode = false,
    cardCollapseAfter = 5
}) => {
    const [Data, setData] = useState(data);
    const [sortConfig, setSortConfig] = useState(defaultSort);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expandedCells, setExpandedCells] = useState({}); // 折疊控制
    const [expandedCards, setExpandedCards] = useState({}); // 手機卡片「顯示更多」控制
    const [copiedCell, setCopiedCell] = useState(null);

    // 拖曳 scroll state
    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const handleMouseLeave = () => setIsDragging(false);
    const handleMouseUp = () => setIsDragging(false);

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.5; // 調整拖曳速度
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const handleSort = (key) => {
        if (sortConfig.key === key) {
            setSortConfig({
                key,
                direction:
                    sortConfig.direction === "asc"
                        ? "desc"
                        : sortConfig.direction === "desc"
                            ? null
                            : "asc",
            });
        } else {
            setSortConfig({ key, direction: "asc" });
        }
    };

    const handleCopy = async (text, cellKey) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedCell(cellKey);
            setTimeout(() => setCopiedCell(null), 1500);
        } catch (err) {
            console.error("複製失敗:", err);
        }
    };

    useEffect(() => {
        setData(data);
    }, [data]);

    const sortedData = [...Data].sort((a, b) => {
        if (!sortConfig.key || !sortConfig.direction) return 0;
        const col = columns.find((c) => c.key === sortConfig.key);
        const valueA = col?.selector ? col.selector(a) : a[sortConfig.key];
        const valueB = col?.selector ? col.selector(b) : b[sortConfig.key];
        const valA = typeof valueA === "string" ? valueA.toLowerCase() : valueA;
        const valB = typeof valueB === "string" ? valueB.toLowerCase() : valueB;
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    // 如果使用 API 分頁，Data 已經是該頁的數據，不需要再切片
    // 如果是前端分頁，需要根據 startIndex/endIndex 切片
    const paginatedData = pagination && !paginationMode ? sortedData.slice(startIndex, endIndex) : sortedData;

    // 折疊文字處理
    const renderCollapsibleCell = (value, rowIndex, colIndex, col, renderedValue, copyValue) => {
        const key = `${rowIndex}-${colIndex}`;
        const isExpanded = expandedCells[key] || false;
        const maxLength = col.maxLength || 50;

        // 不啟用 collapsible
        // if (!col.collapsible) return renderedValue;

        // 折疊字串類型
        if (typeof value === "string") {
            const displayText = col.collapsible ? isExpanded ? value : value.slice(0, maxLength) + "…" : value;
            // if (value.length <= maxLength) return renderedValue;
            return (
                <div className={col.collapsible ? 'collapsible-cell' : 'd-flex justify-content-center'}>
                    <span>{displayText}</span>{" "}
                    <div className="d-flex">
                        {
                            col.collapsible &&
                            <button
                                className="toggle-btn"
                                onClick={() =>
                                    setExpandedCells((prev) => ({ ...prev, [key]: !isExpanded }))
                                }
                            >
                                {isExpanded ? "收起" : "更多"}
                            </button>
                        }
                        {
                            copyValue &&
                            <button
                                className="toggle-btn ms-2"
                                onClick={() =>
                                    handleCopy(value, value)
                                }
                            >
                                {copiedCell === value ? <FontAwesomeIcon icon={faCheckCircle} /> : <FontAwesomeIcon icon={faCopy} />}
                            </button>
                        }
                    </div>
                </div>
            );
        }

        // 折疊陣列類型
        if (Array.isArray(value)) {
            const displayItems = isExpanded ? value : value.slice(0, maxLength);
            return (
                <div className="collapsible-cell-array">
                    {displayItems.map((item, idx) => (
                        <React.Fragment key={idx}>{item}</React.Fragment>
                    ))}
                    {value.length > maxLength ? (
                        <button
                            className="toggle-btn"
                            onClick={() =>
                                setExpandedCells((prev) => ({ ...prev, [key]: !isExpanded }))
                            }
                        >
                            {isExpanded ? "收起" : "更多"}
                        </button>
                    ) : (
                        <button
                            className="toggle-btn ms-4"
                        >
                            {/* {isExpanded ? "收起" : "更多"} */}
                        </button>
                    )}
                </div>
            );
        }

        // 其他型別
        return renderedValue;
    };

    return (
        <>
            <div
                ref={scrollRef}
                className={`datatable-wrapper ${responsive ? "responsive" : ""}`}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                style={{ cursor: isDragging ? "grabbing" : "grab" }}
            >
                <table
                    className={`datatable table ${striped ? "striped" : ""} ${highlightOnHover ? "hover" : ""
                        }`}
                >
                    <thead>
                        <tr>
                            {columns.map((col, index) => (
                                <th
                                    key={index}
                                    onClick={() => col.sortable && handleSort(col.key)}
                                    className={col.sortable ? "sortable" : ""}
                                >
                                    {col.name}
                                    {col.sortable && (
                                        <span className="sort-icon">
                                            {sortConfig.key === col.key
                                                ? sortConfig.direction === "asc"
                                                    ? "▲"
                                                    : sortConfig.direction === "desc"
                                                        ? "▼"
                                                        : "▲▼"
                                                : "▲▼"}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedData.length > 0 ? (
                            paginatedData.map((row, rowIndex) => {
                                const isActionKey = (k) => k === "action" || k === "actions";
                                const nonActionCount = columns.filter((c) => !isActionKey(c.key)).length;
                                const cardCollapsible = !!cardCollapseAfter && nonActionCount > cardCollapseAfter;
                                const isCardExpanded = expandedCards[rowIndex] || false;
                                let dataColSeen = 0;
                                return (
                                    <tr
                                        key={rowIndex}
                                        className={cardCollapsible && !isCardExpanded ? "is-card-collapsed" : ""}
                                    >
                                        {columns.map((col, colIndex) => {
                                            const cellValue = col.selector ? col.selector(row) : row[col.key];
                                            const renderedValue = col.cell ? col.cell(row) : cellValue;
                                            const copyValue = col.copyable;
                                            const isAction = isActionKey(col.key);
                                            let isExtra = false;
                                            if (!isAction) {
                                                dataColSeen += 1;
                                                if (cardCollapseAfter && dataColSeen > cardCollapseAfter) isExtra = true;
                                            }
                                            const cls = [
                                                isAction ? "dt-cell-actions" : "",
                                                isExtra ? "dt-card-extra" : "",
                                            ].filter(Boolean).join(" ");
                                            return (
                                                <td
                                                    key={colIndex}
                                                    data-label={typeof col.name === "string" ? col.name : ""}
                                                    className={cls}
                                                >
                                                    {renderCollapsibleCell(
                                                        cellValue,
                                                        rowIndex,
                                                        colIndex,
                                                        col,
                                                        renderedValue,
                                                        copyValue,
                                                    )}
                                                </td>
                                            );
                                        })}
                                        {cardCollapsible && (
                                            <td className="dt-card-toggle">
                                                <button
                                                    type="button"
                                                    className="dt-card-toggle-btn"
                                                    onClick={() =>
                                                        setExpandedCards((prev) => ({ ...prev, [rowIndex]: !isCardExpanded }))
                                                    }
                                                >
                                                    {isCardExpanded ? "收起" : `顯示更多（${nonActionCount - cardCollapseAfter}）`}
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan={columns.length} className="no-data">
                                    {noDataComponent}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {pagination && sortedData.length > 0 && (
                <div className="pagination d-flex flex-column flex-md-row justify-content-between align-items-center gap-3 gap-md-0 flex-wrap">
                    <div className="rows-per-page">
                        {paginationComponentOptions.rowsPerPageText}:{" "}
                        <select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            {[5, 10, 20, 50].map((num) => (
                                <option key={num} value={num}>
                                    {num}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="page-info">
                        {startIndex + 1} - {Math.min(endIndex, paginationMode && paginationMode.count ? paginationMode.count : sortedData.length)}{" "}
                        {paginationComponentOptions.rangeSeparatorText} {paginationMode && paginationMode.count ? paginationMode.count : sortedData.length}
                    </div>

                    <div className="page-controls">
                        <button onClick={async () => {
                            if (paginationMode && paginationMode.api) {
                                try {
                                    const params = {
                                        page: 1,
                                        limit: rowsPerPage,
                                        direction: sortConfig.direction,
                                        ...paginationMode.searchFields
                                    };
                                    const response = await paginationMode.api(params);
                                    setData(response.data.result);
                                } catch (error) {
                                    console.error('API 呼叫失敗:', error);
                                }
                            }
                            setPage(1);
                        }} disabled={page === 1}>
                            ⏮
                        </button>
                        <button onClick={async () => {
                            const newPage = Math.max(1, page - 1);
                            if (paginationMode && paginationMode.api) {
                                try {
                                    const params = {
                                        page: newPage,
                                        limit: rowsPerPage,
                                        direction: sortConfig.direction,
                                        ...paginationMode.searchFields
                                    };
                                    const response = await paginationMode.api(params);
                                    setData(response.data.result);
                                } catch (error) {
                                    console.error('API 呼叫失敗:', error);
                                }
                            }
                            setPage(newPage);
                        }} disabled={page === 1}>
                            ◀
                        </button>
                        <span>{page}</span>
                        <button
                            onClick={async () => {
                                const maxRows = paginationMode && paginationMode.count ? paginationMode.count : sortedData.length;
                                if (endIndex < maxRows) {
                                    const newPage = page + 1;
                                    if (paginationMode && paginationMode.api) {
                                        try {
                                            const params = {
                                                page: newPage,
                                                limit: rowsPerPage,
                                                direction: sortConfig.direction,
                                                ...paginationMode.searchFields
                                            };
                                            const response = await paginationMode.api(params);
                                            setData(response.data.result);
                                        } catch (error) {
                                            console.error('API 呼叫失敗:', error);
                                        }
                                    }
                                    setPage(newPage);
                                }
                            }}
                            disabled={endIndex >= (paginationMode && paginationMode.count ? paginationMode.count : sortedData.length)}
                        >
                            ▶
                        </button>
                        <button
                            onClick={async () => {
                                const maxRows = paginationMode && paginationMode.count ? paginationMode.count : sortedData.length;
                                const lastPage = Math.ceil(maxRows / rowsPerPage);
                                if (paginationMode && paginationMode.api) {
                                    try {
                                        const params = {
                                            page: lastPage,
                                            limit: rowsPerPage,
                                            direction: sortConfig.direction,
                                            ...paginationMode.searchFields
                                        };
                                        const response = await paginationMode.api(params);
                                        setData(response.data.result);
                                    } catch (error) {
                                        console.error('API 呼叫失敗:', error);
                                    }
                                }
                                setPage(lastPage);
                            }}
                            disabled={endIndex >= (paginationMode && paginationMode.count ? paginationMode.count : sortedData.length)}
                        >
                            ⏭
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export { DataTable };
